import { useEffect } from "react";

/** App-wide arrow keys. Works on every grid without per-table wiring:
 *  ↑ / ↓  move between rows (table rows, expandable row buttons, cards marked data-kb-row)
 *  → / ←  expand / collapse the active row (aria-expanded), or step next / previous record
 *         when a focus view exposes [data-kb-next] / [data-kb-prev]
 *  Enter  opens the active row.  Ignored while typing in a field. */
const ROW = 'table tbody tr, [data-kb-row], [role="row"], button[aria-expanded], [data-testid*="-row-"]';
const EDITABLE = "input, textarea, select, [contenteditable=true], [role=combobox], [role=menu], [role=listbox], [role=dialog] input";

const visible = (el) => el.offsetParent !== null && el.getClientRects().length > 0;
const rows = () => Array.from(document.querySelectorAll(ROW)).filter(visible)
  .filter((el, i, all) => !all.some((o) => o !== el && o.contains(el)));   // innermost only when nested

let active = null;
const mark = (el) => {
  if (active && active !== el) active.classList.remove("kb-active");
  active = el;
  if (!el) return;
  el.classList.add("kb-active");
  if (!el.hasAttribute("tabindex")) el.setAttribute("tabindex", "-1");
  el.focus({ preventScroll: true });
  el.scrollIntoView({ block: "nearest" });
};

export default function KeyboardNav() {
  useEffect(() => {
    const onKey = (e) => {
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      if (!["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Enter"].includes(e.key)) return;
      const t = e.target;
      if (t && (t.matches?.(EDITABLE) || t.closest?.("[contenteditable=true], [role=dialog] input, [role=menu], [role=listbox]"))) return;
      if (document.querySelector("[role=dialog][data-state=open], .fixed.inset-0.z-50")) return; // a dialog owns the keys
      if (document.querySelector("[data-kb-own]")) return;   // a view with its own key map (WH-DESK-1 pull desk) owns the keys
      const list = rows();
      if (!active || !document.contains(active) || !visible(active)) {
        active = list.find((el) => el === document.activeElement || el.contains(document.activeElement)) || null;
      }
      const idx = active ? list.indexOf(active) : -1;
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        if (!list.length) return;
        e.preventDefault();
        const next = e.key === "ArrowDown" ? Math.min(list.length - 1, idx + 1) : Math.max(0, idx - 1);
        mark(list[next]);
        return;
      }
      if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
        const step = document.querySelector(e.key === "ArrowRight" ? "[data-kb-next]" : "[data-kb-prev]");
        if (step && visible(step) && !step.disabled) { e.preventDefault(); step.click(); return; }
        if (!active) return;
        const toggle = active.matches("[aria-expanded]") ? active : active.querySelector("[aria-expanded]");
        if (!toggle) return;
        const open = toggle.getAttribute("aria-expanded") === "true";
        if ((e.key === "ArrowRight" && !open) || (e.key === "ArrowLeft" && open)) { e.preventDefault(); toggle.click(); }
        return;
      }
      if (e.key === "Enter" && active && !active.matches("button, a")) {
        const btn = active.querySelector("button[aria-expanded], a, button");
        if (btn) { e.preventDefault(); btn.click(); } else { active.click(); }
      }
    };
    // A click into a field inside a row must keep focus in the field, not hand it to the row.
    const onClick = (e) => { const r = e.target.closest?.(ROW); if (r && !e.target.matches?.(EDITABLE)) mark(r); };
    document.addEventListener("keydown", onKey);
    document.addEventListener("click", onClick, true);
    return () => { document.removeEventListener("keydown", onKey); document.removeEventListener("click", onClick, true); };
  }, []);
  return null;
}
